// web/src/pages/Stock.tsx
import { useEffect, useMemo, useState } from "react";

import { Shell } from "../components/layout/Shell";
import { EnableForm } from "../components/stock/EnableForm";
import { StockTable, type Sort } from "../components/stock/StockTable";
import {
  call,
  type Me,
  type PushPreview,
  type StockReply,
  type StockRow,
  type StopReply,
} from "../lib/api";

/**
 * Раздел «Сток».
 *
 * Кнопки «Выгрузить» здесь НЕТ намеренно. Маршрут `/api/stock/push/apply`
 * заведён и проверен, но первые боевые прогоны идут CLI-путём под
 * присмотром: песочницы у Ozon нет, и одна кнопка, до которой можно
 * дотянуться мышью, — не то, чем стоит начинать менять боевой кабинет.
 */
export function Stock({ me, onLeave }: { me: Me; onLeave: () => void }) {
  const [data, setData] = useState<StockReply | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [enabling, setEnabling] = useState(false);
  const [sort, setSort] = useState<Sort>(null);
  const [modelId, setModelId] = useState("");
  const [cardId, setCardId] = useState("");
  // «Только включённые» и клик по ячейке статуса — ОДНО состояние с двумя
  // входами: два независимых переключателя одного и того же спорили бы друг
  // с другом.
  const [status, setStatus] = useState<"all" | "on" | "off">("all");

  async function load() {
    const reply = await call<StockReply>("/api/stock");
    if (reply.results) setData(reply.results);
    else setNote(reply.error[0]?.message ?? "Не удалось прочитать раздел");
  }

  useEffect(() => {
    void load();
  }, []);

  // Значение ячейки по ключу колонки — общее чтение для сортировки: колонка
  // города не поле `StockRow`, а запись в словаре `korob`, отсюда особый
  // случай `city:<id>`.
  function cell(row: StockRow, key: string): number | string {
    if (key.startsWith("city:")) return row.korob[key.slice(5)] ?? 0;
    const value = (row as unknown as Record<string, unknown>)[key];
    if (typeof value === "number") return value;
    if (typeof value === "boolean") return value ? 1 : 0;
    return String(value ?? "");
  }

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const needle = query.trim().toLowerCase();
    const kept = all.filter((row: StockRow) => {
      // «Нет на Ozon» — тот самый отбор, ради которого раздел и открывают:
      // товар лежит в коробе города, где у нас есть склад, а на Ozon по нему
      // ноль, значит заказ туда не придёт никогда.
      if (onlyMissing && !(row.fbo === 0 && row.korob_working > 0))
        return false;
      if (status === "on" && !row.fbs_enabled) return false;
      if (status === "off" && row.fbs_enabled) return false;
      if (modelId && String(row.model_id ?? "") !== modelId) return false;
      if (cardId && String(row.card_id ?? "") !== cardId) return false;
      if (!needle) return true;
      return (
        row.offer_id.toLowerCase().includes(needle) ||
        String(row.sku).includes(needle) ||
        row.name.toLowerCase().includes(needle)
      );
    });
    if (!sort) return kept;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...kept].sort((a, b) => {
      const x = cell(a, sort.key);
      const y = cell(b, sort.key);
      if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
      return String(x).localeCompare(String(y), "ru") * sign;
    });
  }, [data, onlyMissing, status, modelId, cardId, query, sort]);

  const units = useMemo(
    () => rows.reduce((sum, row) => sum + row.available, 0),
    [rows],
  );

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => {
      const everyChosen =
        rows.length > 0 && rows.every((r) => prev.has(r.ozon_product_id));
      return everyChosen
        ? new Set()
        : new Set(rows.map((r) => r.ozon_product_id));
    });
  }

  // Отбор кликом по значению ячейки — тот же смысл, что и поля/фишки сверху,
  // только адресован конкретной строкой таблицы.
  function filterBy(kind: "model" | "card" | "status", value: string) {
    if (kind === "model") setModelId(value);
    else if (kind === "card") setCardId(value);
    else setStatus(value as "on" | "off");
  }

  // Первый клик — по убыванию: колонки здесь числовые, и вопрос к ним «где
  // больше всего», а не «где меньше». Третий клик снимает сортировку и
  // возвращает порядок каталога.
  function toggleSort(key: string) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: "desc" };
      if (prev.dir === "desc") return { key, dir: "asc" };
      return null;
    });
  }

  // Название `applyFbsStatus`, а не `setStatus`: имя `setStatus` занято
  // сеттером состояния отбора «статус FBS» (клик по ячейке / фишка) — это
  // разные вещи, вызов API и локальный фильтр, и делить имя им нельзя.
  async function applyFbsStatus(enabled: boolean, locations: number[]) {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      const reply = await call<{ touched: number }>("/api/stock/fbs-status", {
        products: [...selected],
        enabled,
        locations,
      });
      if (reply.error.length) {
        setNote(reply.error[0]?.message ?? "Не удалось изменить статус");
      } else {
        setSelected(new Set());
        setEnabling(false);
        setNote(
          enabled
            ? `Включено строк: ${reply.results?.touched ?? 0}`
            : `Выключено строк: ${reply.results?.touched ?? 0}, города сохранены`,
        );
        await load();
      }
    } finally {
      // Сбрасываем при любом исходе: иначе сбой оставит кнопки навсегда
      // задизейбленными, и это будет выглядеть как «раздел завис».
      setBusy(false);
    }
  }

  async function stop() {
    // Спрашиваем подтверждение, хотя остановка и безопасна: она снимает с
    // витрины ВЕСЬ товар сразу, и случайное нажатие стоит продаж.
    if (busy) return;
    if (
      !window.confirm(
        "Обнулить остатки FBS на всех складах и выключить FBS у всех товаров?",
      )
    )
      return;
    setBusy(true);
    setNote("");
    try {
      const reply = await call<StopReply>("/api/stock/stop", {});
      if (reply.error.length)
        setNote(reply.error[0]?.message ?? "Остановить не удалось");
      else {
        const runs = reply.results?.runs ?? [];
        setNote(
          runs.length === 0
            ? "Гасить было нечего: ненулевых остатков на складах нет."
            : runs
                .map(
                  (r) =>
                    `Склад ${r.warehouse_id}: ${r.status}, позиций ${r.positions}, ` +
                    `принято ${r.accepted}, отвергнуто ${r.rejected}`,
                )
                .join(" · "),
        );
        await load();
      }
    } finally {
      setBusy(false);
    }
  }

  async function preview(location: number) {
    setBusy(true);
    setNote("");
    try {
      const reply = await call<PushPreview>("/api/stock/push/preview", {
        location,
      });
      if (reply.error.length)
        setNote(reply.error[0]?.message ?? "Сухой прогон не удался");
      else if (reply.results)
        setNote(
          `Сухой прогон, склад ${reply.results.warehouse_id}: позиций ` +
            `${reply.results.positions}, штук ${reply.results.units}, из них нулевых ` +
            `${reply.results.zeros}. Ничего не отправлено.`,
        );
    } finally {
      setBusy(false);
    }
  }

  const locations = data?.locations ?? [];
  const warehouses = data?.warehouses ?? [];

  // Города берутся ИЗ ДАННЫХ, а не из списка в коде: появится короб в
  // четвёртом городе — появится колонка. Порядок — сначала города со складом
  // FBS, потом остальные: у последних короба в расчёт не идут, и читать их
  // вперемешку значило бы сравнивать разное.
  const cities = useMemo(() => {
    // Зависимость — только `data`: `locations` ниже это `data?.locations ??
    // []`, то есть новый массив на КАЖДОМ рендере, и если завести его в
    // зависимости, мемоизация не срабатывает вовсе — обход всех строк идёт
    // заново каждый раз. Читаем `data?.locations` внутри самого useMemo.
    const seen = new Set<string>();
    for (const row of data?.rows ?? [])
      for (const key of Object.keys(row.korob)) seen.add(key);
    const label = new Map(
      (data?.locations ?? []).map((l) => [String(l.id), l.short || l.name]),
    );
    const working = new Set(
      (data?.warehouses ?? []).map((w) => String(w.location_id)),
    );
    return [...seen]
      .sort((a, b) => {
        const byWork = Number(working.has(b)) - Number(working.has(a));
        return byWork !== 0 ? byWork : Number(a) - Number(b);
      })
      .map((id) => ({ id, label: label.get(id) ?? id }));
  }, [data]);

  // Выход обязан гасить сессию на СЕРВЕРЕ, а не только в состоянии
  // компонента: иначе cookie остаётся живой, и «выйти» на этой странице
  // означало бы не то же, что на главной.
  async function leave() {
    await call("/api/logout", {});
    onLeave();
  }

  return (
    <Shell me={me} onLeave={leave} active="stock">
      <div className="flex h-full min-h-0 flex-col" data-annot="экран Сток">
        <div
          className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 text-[13px]"
          data-annot="отбор"
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Артикул, SKU или название"
            className="h-8 w-64 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg outline-none focus:border-border-strong"
            data-annot="поиск"
          />
          <label
            className="flex items-center gap-1.5 text-fg"
            data-annot="флажок Нет на Ozon"
          >
            <input
              type="checkbox"
              checked={onlyMissing}
              onChange={(e) => setOnlyMissing(e.target.checked)}
            />
            Нет на Ozon
          </label>
          <label
            className="flex items-center gap-1.5 text-fg"
            data-annot="флажок Только включённые"
          >
            <input
              type="checkbox"
              checked={status === "on"}
              onChange={(e) => setStatus(e.target.checked ? "on" : "all")}
            />
            Только включённые
          </label>
          <input
            value={modelId}
            onChange={(e) => setModelId(e.target.value.replace(/\D/g, ""))}
            placeholder="Model ID"
            inputMode="numeric"
            className="h-8 w-28 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg outline-none focus:border-border-strong"
            data-annot="поле Model ID"
          />
          <input
            value={cardId}
            onChange={(e) => setCardId(e.target.value.replace(/\D/g, ""))}
            placeholder="Card ID"
            inputMode="numeric"
            className="h-8 w-28 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-fg outline-none focus:border-border-strong"
            data-annot="поле Card ID"
          />
          <span className="text-fg-faint" data-annot="счётчик строк">
            строк {rows.length}, расчёт {units} шт, выбрано {selected.size}
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <button
              type="button"
              disabled={selected.size === 0 || busy}
              onClick={() => setEnabling(true)}
              className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg hover:bg-bg-hover disabled:opacity-50"
              data-annot="кнопка Включить FBS"
            >
              Включить FBS
            </button>
            <button
              type="button"
              disabled={selected.size === 0 || busy}
              onClick={() => void applyFbsStatus(false, [])}
              className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg hover:bg-bg-hover disabled:opacity-50"
              data-annot="кнопка Выключить"
            >
              Выключить
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void stop()}
              className="h-8 rounded-[var(--radius-ui)] border border-err px-3 text-err hover:bg-err-bg disabled:opacity-50"
              title="Обнулить остатки FBS на всех складах и выключить FBS у всех товаров. Города сохранятся"
              data-annot="кнопка Остановить FBS"
            >
              Остановить FBS
            </button>
            {warehouses.map((wh) => (
              <button
                key={wh.location_id}
                type="button"
                disabled={busy}
                onClick={() => void preview(wh.location_id)}
                className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-fg-faint hover:bg-bg-hover hover:text-fg disabled:opacity-50"
                title={`Показать, что уедет на склад «${wh.name}». Ничего не отправляет`}
                data-annot={`кнопка сухого прогона ${wh.name}`}
              >
                Сухой прогон · {wh.name}
              </button>
            ))}
          </div>
        </div>
        {(modelId || cardId || status !== "all") && (
          <div
            className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-[13px]"
            data-annot="активный отбор"
          >
            {modelId && (
              <button
                type="button"
                onClick={() => setModelId("")}
                className="h-6 rounded-[var(--radius-ui)] border border-border px-2 text-fg-muted hover:bg-bg-hover"
                data-annot="фишка Model ID"
              >
                Model {modelId} ×
              </button>
            )}
            {cardId && (
              <button
                type="button"
                onClick={() => setCardId("")}
                className="h-6 rounded-[var(--radius-ui)] border border-border px-2 text-fg-muted hover:bg-bg-hover"
                data-annot="фишка Card ID"
              >
                Карточка {cardId} ×
              </button>
            )}
            {status !== "all" && (
              <button
                type="button"
                onClick={() => setStatus("all")}
                className="h-6 rounded-[var(--radius-ui)] border border-border px-2 text-fg-muted hover:bg-bg-hover"
                data-annot="фишка статус FBS"
              >
                FBS {status === "on" ? "вкл" : "выкл"} ×
              </button>
            )}
          </div>
        )}
        {enabling && (
          <EnableForm
            count={selected.size}
            locations={locations}
            busy={busy}
            onCancel={() => setEnabling(false)}
            onConfirm={(chosen) => void applyFbsStatus(true, chosen)}
          />
        )}
        {note && (
          <div
            className="border-b border-border px-4 py-2 text-[13px] text-fg-muted"
            data-annot="строка ответа"
          >
            {note}
          </div>
        )}
        <StockTable
          rows={rows}
          selected={selected}
          onToggle={toggle}
          onToggleAll={toggleAll}
          cities={cities}
          sort={sort}
          onSort={toggleSort}
          onFilter={filterBy}
        />
      </div>
    </Shell>
  );
}
